import "./styles.scss";

import { ButtonIconPosition, ButtonSize } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import { Chip, ChipBackgroundColor, ChipSize } from "@amzn/stencil-react-components/chip";
import { Col, GridItem, GridItemProps, Hr, Row, View } from "@amzn/stencil-react-components/layout";
import { StarRating } from "@amzn/stencil-react-components/star-rating";
import { Text } from "@amzn/stencil-react-components/text";
import { TOOLTIP_POSITION, withTooltip } from "@amzn/stencil-react-components/tooltip";
import { IconInformationFill, IconShow, IconSize } from "@amzn/stencil-react-icons";
import IconCheckCircleFill from "@amzn/stencil-react-icons/icons/icon-check-circle-fill";
import IconExternalLink from "@amzn/stencil-react-icons/icons/icon-external-link";
import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import Button, { ButtonVariant } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { Link } from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { useLists } from "common/context/lists";
import { TrackClickInteraction } from "common/helpers";
import { AiForMeClickPayload, calculateBucket, SearchTelemetryMetadata } from "common/helpers/search-analytics";
import { useVoteSubmission, VoteHandler } from "common/hooks";
import { getString } from "common/uistringlabels/uiStringUtils";
import { config, PERMISSIONS } from "config";
import { AIForMeTool } from "lib/models/aiforme-models";
import { ListModel } from "lib/models/list-models";
import { CatalogicalService } from "lib/services";
import { UserPermissions } from "lib/services";
import { FeedbackModelRecord } from "lib/services/catalogical-models";
import { RecentlyVisitedService } from "lib/services/recently-visited-service";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import categoriesOverride from "../../../configuration/AIForMe/categoriesOverride.json";
import { ListActions } from "../ListActions/ListActions";
import { ProductReviewsFlyout } from "../ProductReviews/ProductReviewsFlyout";
import { StarAverageRating, StarAverageRatingSize } from "../Ratings/StarAverageRating";
import { StarDetailedRating, StarDetailedRatingSize } from "../Ratings/StarDetailedRating";
import { SubmitReviewForm } from "../SubmitReviewForm";
import { DataClassificationChip } from "./AiForMeDataClassificationChip";
import { CompareButton } from "./Compare/CompareButton";
import { formatViewCount, resolveDisplayName } from "./helpers";
import { formatLastUsedDate } from "./helpers/data-helpers";

const IconCheckCircleFillWithTooltip = withTooltip()(IconCheckCircleFill);
const IconInformationFillWithTooltip = withTooltip()(IconInformationFill);
const ChipWithTooltip = withTooltip()(Chip);

const TOOLTIP_OPEN_DELAY_MS = 100;
const HOVER_SESSION_RESET_DELAY_MS = 300;
const DESCRIPTION_MAX_WORDS = 20;
const DESCRIPTION_SHOW_MORE = "Show more";
const DESCRIPTION_SHOW_LESS = "Show less";

type GridSpan = {
  lg: GridItemProps["lg"];
  md: GridItemProps["md"];
};

export interface AIForMeItemProps {
  item: AIForMeTool;
  index: number;
  sortingPriorityTag?: string;
  voteHandler: VoteHandler;
  client: CatalogicalService<AIForMeTool>;
  userVote?: FeedbackModelRecord;
  lists?: ListModel[];
  gridSpan?: GridSpan;
  /** Whether this tool is selected for comparison */
  isCompareSelected: boolean;
  /** Whether the compare button should be disabled (max tools reached and not selected) */
  isCompareDisabled: boolean;
  /** Callback when tool is added to comparison */
  onCompareAdd: (tool: AIForMeTool) => void;
  /** Callback when tool is removed from comparison */
  onCompareRemove: (taxonomyId: string) => void;
  /** Callback when a review flyout opens or closes */
  onReviewFlyoutToggle?: (isOpen: boolean) => void;
  /** Whether to hide the tags row */
  hideTags?: boolean;
  /** Whether to hide the compare button */
  hideCompare?: boolean;
  /** Optional last-used date string for recently used tools */
  lastUsedDate?: string;
  /** Callback to track click interactions when user clicks "Learn More" */
  onTrackClick: TrackClickInteraction;
  /** 1-based rank position in search results */
  rank?: number;
  /** Search telemetry metadata for tracking */
  searchMetadata?: SearchTelemetryMetadata;
  isRecommended?: boolean;
  recommendedJobFamilies?: string[];
  /** Callback fired after a visit is successfully recorded. */
  onVisitRecorded?: (tool: AIForMeTool, visitedAt: string) => void;
}

export const AiForMeItem: FC<AIForMeItemProps> = React.memo((props: AIForMeItemProps) => {
  const toggleAiForMeReviews = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_AI_FOR_ME_REVIEWS);
  const toggleLists = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS);
  const taxId = new TaxonomyIdItem(props.item.taxonomyId);
  const displayName = resolveDisplayName(props.item);
  const linkTitle = `External link to Learn more about ${displayName}`;

  const { yourLists } = useLists();
  const lists = props.lists ?? yourLists;
  const recentlyVisitedService = useMemo(() => new RecentlyVisitedService(), []);

  // Compare functionality - received via props to avoid context subscription
  const isSelected = props.isCompareSelected;
  const isCompareDisabled = props.isCompareDisabled;

  // Handles write and check reviews flyout open state
  const [openWriteReviewFlyout, setOpenWriteReviewFlyout] = useState(false);
  const [openCheckReviewsFlyout, setOpenCheckReviewFlyout] = useState(false);
  const [reviewRefreshTrigger, setReviewRefreshTrigger] = useState(0);

  // Notify parent when any review flyout opens/closes to hide compare bar
  const anyReviewFlyoutOpen = openCheckReviewsFlyout || openWriteReviewFlyout;
  const prevReviewFlyoutOpenRef = useRef(false);

  useEffect(() => {
    if (anyReviewFlyoutOpen !== prevReviewFlyoutOpenRef.current) {
      prevReviewFlyoutOpenRef.current = anyReviewFlyoutOpen;
      props.onReviewFlyoutToggle?.(anyReviewFlyoutOpen);
    }
  }, [anyReviewFlyoutOpen]);

  useEffect(() => {
    return () => {
      if (prevReviewFlyoutOpenRef.current) {
        props.onReviewFlyoutToggle?.(false);
      }
    };
  }, []);

  // Description expand state
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Tracks if category hover event has been sent
  const categoryHoverTrackedRef = useRef(false);
  const categoryHoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const categoryTrackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (categoryHoverTimeoutRef.current) clearTimeout(categoryHoverTimeoutRef.current);
      if (categoryTrackTimeoutRef.current) clearTimeout(categoryTrackTimeoutRef.current);
    };
  }, []);

  /* istanbul ignore next */
  const trackUxmCustomEventOnVote = (ratingValue: number) => {
    const trackerId = config.analytics.uxmAppId;
    const event = {
      taxonomyId: props.item.taxonomyId.toString(),
      title: props.item.title,
      rating: ratingValue,
    };

    trackCustomEvent(event, "aiforme-vote", trackerId);
  };

  const {
    rating,
    setRating,
    isSubmittingVote,
    errorMessage,
    itemFeedbackStats,
    setItemFeedbackStats,
    isLoadingFeedback,
    doPostFeedback,
    handleVote,
  } = useVoteSubmission({
    taxonomyId: props.item.taxonomyId,
    voteHandler: props.voteHandler,
    client: props.client,
    previousRating: props.userVote?.rating,
    itemFeedback: props.item.feedback,
    onTrackVote: trackUxmCustomEventOnVote,
  });

  const dataClassification = props.item.dataClassification ?? "Public";
  const securityReviewed =
    props.item.securityReviewed === "yes" ? "Yes" : props.item.securityReviewed === "no" ? "No" : undefined;

  // This is a temporary measure to change the display name of a category
  // This will require a refactor on Catalogical side and this was required as urgent
  const getOptionDisplayName = (option: string) => (categoriesOverride as { [key: string]: string })[option] ?? option;

  // Handles category hover tracking - prevents duplicate events during tooltip navigation
  const handleCategoryEnter = useCallback(() => {
    if (categoryHoverTimeoutRef.current) {
      clearTimeout(categoryHoverTimeoutRef.current);
      categoryHoverTimeoutRef.current = null;
    }
    if (!categoryHoverTrackedRef.current) {
      if (categoryTrackTimeoutRef.current) {
        clearTimeout(categoryTrackTimeoutRef.current);
      }
      categoryTrackTimeoutRef.current = setTimeout(() => {
        categoryHoverTrackedRef.current = true;
        const trackerId = config.analytics.uxmAppId;
        const filteredCategories =
          props.item.categories
            ?.filter((category) => !props.sortingPriorityTag?.startsWith(category))
            .sort((a, b) => a.localeCompare(b)) ?? [];
        const event = {
          taxonomyId: props.item.taxonomyId.toString(),
          title: props.item.title,
          remainingCategoriesCount: Math.max(0, filteredCategories.length - 3),
          remainingCategories: filteredCategories.slice(3),
        };
        trackCustomEvent(event, "aiforme-categories-hover", trackerId);
      }, TOOLTIP_OPEN_DELAY_MS);
    }
  }, [props.item.taxonomyId, props.item.title, props.item.categories, props.sortingPriorityTag]);

  // Resets hover tracking after user leaves the chip/tooltip area
  const handleCategoryLeave = useCallback(() => {
    if (categoryTrackTimeoutRef.current) {
      clearTimeout(categoryTrackTimeoutRef.current);
      categoryTrackTimeoutRef.current = null;
    }
    if (categoryHoverTimeoutRef.current) {
      clearTimeout(categoryHoverTimeoutRef.current);
    }
    categoryHoverTimeoutRef.current = setTimeout(() => {
      categoryHoverTrackedRef.current = false;
    }, HOVER_SESSION_RESET_DELAY_MS);
  }, []);

  /**
   * Will create an array of categories in gray labels. When exceeding 3 categories, will create a 4th label that indicates N number
   * of categories left to see and will add them to a tooltip that triggers on hover
   * @param categories categories to create gray labels
   * @returns array of category labels
   */
  const getCategoryTags = (categories: string[]) => {
    const tags = [];
    // sort alphabetically
    const filteredCategories = categories
      .filter((category) => !props.sortingPriorityTag?.startsWith(category))
      .sort((a, b) => a.localeCompare(b));
    const visibleCategories = filteredCategories.slice(0, 3);
    const remainingCategories = filteredCategories.slice(3);

    for (const category of visibleCategories) {
      tags.push(
        <div key={category} title="Category" className="chip-category">
          <Chip key={category} data-testid={category} size={ChipSize.ExtraSmall}>
            {getOptionDisplayName(category)}
          </Chip>
        </div>
      );
    }

    if (remainingCategories.length > 0) {
      const tooltipContent = (
        <Col gridGap={4}>
          {remainingCategories.map((cat) => (
            <Chip key={cat} size={ChipSize.ExtraSmall}>
              {getOptionDisplayName(cat)}
            </Chip>
          ))}
        </Col>
      );

      tags.push(
        <div
          key="more-categories"
          title="More categories"
          className="chip-category"
          onMouseEnter={handleCategoryEnter}
          onMouseLeave={handleCategoryLeave}
        >
          <ChipWithTooltip
            data-testid="more-categories"
            size={ChipSize.ExtraSmall}
            tooltipText={tooltipContent}
            tooltipPosition={TOOLTIP_POSITION.BOTTOM}
          >
            +{remainingCategories.length} more
          </ChipWithTooltip>
        </div>
      );
    }
    return tags;
  };

  const toggleDescription = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDescriptionExpanded((prev) => {
        const action = prev ? "collapse" : "expand";
        trackCustomEvent(
          { taxonomyId: props.item.taxonomyId.toString(), title: props.item.title, action },
          "aiforme-description-toggle",
          config.analytics.uxmAppId
        );
        return !prev;
      });
    },
    [props.item.taxonomyId, props.item.title]
  );

  const descriptionWords = useMemo(
    () => props.item.description?.split(/\s+/).filter(Boolean) ?? [],
    [props.item.description]
  );

  const renderDescription = () => {
    if (descriptionWords.length <= DESCRIPTION_MAX_WORDS) {
      return <>{props.item.description}</>;
    }
    const truncated = descriptionWords.slice(0, DESCRIPTION_MAX_WORDS).join(" ").trimEnd();
    return (
      <span
        role="button"
        tabIndex={0}
        aria-expanded={isDescriptionExpanded}
        data-testid={isDescriptionExpanded ? "description-expanded" : "description-truncated"}
        className="description-truncated"
        onClick={toggleDescription}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") toggleDescription(e);
        }}
      >
        {isDescriptionExpanded ? props.item.description : `${truncated}... `}
        <span
          className="description-toggle-link"
          data-testid={isDescriptionExpanded ? "description-show-less" : "description-see-more"}
        >
          {isDescriptionExpanded ? ` ${DESCRIPTION_SHOW_LESS}` : DESCRIPTION_SHOW_MORE}
        </span>
      </span>
    );
  };

  const getTags = () => {
    const tags = [];
    if (props.sortingPriorityTag)
      tags.push(
        <div key="sortingPriorityTag" title="Assessment status" className="chip-category">
          <Chip
            key="sortingPriorityTag"
            data-testid="sortingPriorityTag"
            size={ChipSize.ExtraSmall}
            backgroundColor={ChipBackgroundColor.Green}
          >
            {props.sortingPriorityTag}
          </Chip>
        </div>
      );
    if (dataClassification)
      tags.push(
        <div key="dataClassification" title="Data classification" className="chip-category">
          <DataClassificationChip
            label={getString("aiForMe.tags.dataClassification", { dataClassification: dataClassification })}
          />
        </div>
      );

    // TODO: Uncomment section to add "Security reviewed" tag
    // if (securityReviewed)
    //   tags.push(
    //     <div key="securityReviewed" title="Security reviewed status" className="chip-category">
    //       <Chip
    //         key="securityReviewed"
    //         data-testid="securityReviewed"
    //         size={ChipSize.ExtraSmall}
    //         backgroundColor={ChipBackgroundColor.Yellow}
    //       >
    //         {getString("aiForMe.tags.securityReviewed", { securityReviewed: securityReviewed })}
    //       </Chip>
    //     </div>
    //   );

    const categories = getCategoryTags(props.item.categories ?? []);
    categories.forEach((tag) => tags.push(tag));

    if (props.item.basePlatform)
      tags.push(
        <div key="basePlatform" title="Base Platform" className="chip-category">
          <Chip
            key="basePlatform"
            data-testid="basePlatform"
            size={ChipSize.ExtraSmall}
            backgroundColor={ChipBackgroundColor.Purple}
          >
            {props.item.basePlatform}
          </Chip>
        </div>
      );

    if (props.item.status === "UPDATED")
      tags.push(
        <div key="status" title="Status" className="chip-category">
          <Chip key="status" data-testid="status" size={ChipSize.ExtraSmall} backgroundColor={ChipBackgroundColor.Red}>
            {getString("aiForMe.tags.updatedUnderReview")}
            <IconInformationFillWithTooltip
              tooltipText={<Text>{getString("aiForMe.tags.UnderReviewTooltip")}</Text>}
              size={IconSize.ExtraSmall}
            />
          </Chip>
        </div>
      );
    if (props.item.tags)
      for (const [index, itemTag] of props.item.tags.entries()) {
        tags.push(
          <div key={`custom-tag-container-${index}`} title="Tag" className="chip-category">
            <Chip
              key={`custom-tag-${index}`}
              data-testid={`custom-tag-${index}`}
              size={ChipSize.ExtraSmall}
              backgroundColor={ChipBackgroundColor.Yellow}
            >
              {itemTag}
            </Chip>
          </div>
        );
      }

    return tags;
  };

  const buttonCheckReviews = (buttonText?: string, touchpoint?: string) => (
    <Button
      variant={ButtonVariant.Tertiary}
      onClick={() => setOpenCheckReviewFlyout(true)} // set to opposite value
      data-test-id={"check-reviews-button"}
      touchpoint={touchpoint ?? "ai-for-me-check-reviews"}
      aria-label={`${buttonText ?? "Check reviews"} for ${displayName}`}
    >
      {buttonText ?? "Check reviews"}
    </Button>
  );

  const renderViewReviewsFlyout = () => {
    return (
      <ProductReviewsFlyout
        productName={displayName}
        titleText={"Reviews"}
        isOpen={openCheckReviewsFlyout}
        setIsOpen={setOpenCheckReviewFlyout}
        setOpenWriteReviewFlyout={setOpenWriteReviewFlyout}
        feedback={itemFeedbackStats}
        taxonomyId={taxId}
        domain="aiforme"
        refreshTrigger={reviewRefreshTrigger}
      />
    );
  };

  const renderReviewSubmissionFlyout = () => {
    return (
      <SubmitReviewForm
        productName={displayName}
        titleText={"Submit review"}
        isOpen={openWriteReviewFlyout}
        setIsOpen={setOpenWriteReviewFlyout}
        previousRating={rating}
        setPreviousRating={setRating}
        isLoading={isLoadingFeedback}
        doPostFeedback={doPostFeedback}
        setItemFeedback={setItemFeedbackStats}
        taxonomyId={taxId}
        domain="aiforme"
        onReviewSubmitted={() => setReviewRefreshTrigger((prev) => prev + 1)}
      />
    );
  };

  const renderRatings = () => {
    const popoverContent = (
      <StarDetailedRating
        size={StarDetailedRatingSize.Small}
        averageRating={itemFeedbackStats?.averageRating}
        voteNumber={itemFeedbackStats?.numberOfRatings}
        ratingCounts={itemFeedbackStats?.ratingCounts}
        button={buttonCheckReviews()}
      />
    );

    return (
      <Row>
        <StarAverageRating
          size={StarAverageRatingSize.Small}
          averageRating={itemFeedbackStats?.averageRating}
          voteNumber={itemFeedbackStats?.numberOfRatings}
          popoverContent={toggleAiForMeReviews ? popoverContent : undefined}
          touchpoint="ai-for-me-view-ratings"
        />
        <Text fontSize="T100" className="click-count" style={{ marginLeft: "16px" }}>
          <Row columnGap={2} alignItems="center">
            <IconShow size={IconSize.Small} />
            {formatViewCount(props.item.catalogicalGenerated?.interactions?.clicks)} views
          </Row>
        </Text>
      </Row>
    );
  };

  const ReviewsButtonComponent = () =>
    buttonCheckReviews(
      getString("aiForMe.reviewsWithCount", { count: String(itemFeedbackStats?.numberOfFeedback ?? 0) }),
      "ai-for-me-user-reviews"
    );
  const ReviewsButton = withTooltip()(ReviewsButtonComponent);

  /**
   * Handle "Learn More" click: track interaction and record visit
   */
  const handleLearnMoreClick = useCallback(() => {
    const id = new TaxonomyIdItem(props.item.taxonomyId);
    props.onTrackClick(id);
    recentlyVisitedService
      .recordVisit(props.item.taxonomyId, "aiforme")
      .then((res) => {
        props.onVisitRecorded?.(props.item, res.visitedAt);
      })
      .catch(() => {});
    window.open(props.item.learnMoreLink, "_blank", "noopener,noreferrer");
  }, [
    props.item.taxonomyId,
    props.item.learnMoreLink,
    props.onTrackClick,
    props.onVisitRecorded,
    recentlyVisitedService,
  ]);

  /**
   * Build custom data for "Learn More" link telemetry
   */
  const getLearnMoreCustomData = (): AiForMeClickPayload => {
    const bucket = calculateBucket(props.item, "aiforme", props.isRecommended);

    const customData: AiForMeClickPayload = {
      taxonomyId: taxId.toString(),
      title: props.item.title,
      rank: props.rank,
      score: props.item.score,
      queryId: props.searchMetadata?.queryId ?? "-",
      visibleResultsCount: props.searchMetadata?.visibleResultCount,
      totalResultsCount: props.searchMetadata?.resultCount,
      bucket,
      sortingPriorityTag: props.sortingPriorityTag,
      isRecommended: props.isRecommended ?? false,
      recommendedJobFamilies: props.recommendedJobFamilies,
    };

    return customData;
  };

  const renderFooter = () => {
    if (toggleAiForMeReviews) {
      return (
        <Col style={{ marginTop: "16px" }}>
          {errorMessage && <Text fontSize="smaller">{errorMessage}</Text>}
          {isSubmittingVote && <Text fontSize="smaller">{getString("aiForMe.submitting")}</Text>}
          {!isSubmittingVote && !errorMessage && (
            <Row alignItems="center" gridGap={5}>
              <Text fontSize="smaller">{getString("aiForMe.quickRate")}</Text>
              <StarRating name="star-rating" data-testid="star-rating" value={rating} onChange={handleVote} />
            </Row>
          )}
          <Row alignItems="center" justifyContent="space-between" gridGap={0} padding={0} style={{ marginTop: "16px" }}>
            <ReviewsButton tooltipText="Check and write reviews" />
            {!props.hideCompare && (
              <CompareButton
                tool={props.item}
                isSelected={isSelected}
                disabled={isCompareDisabled}
                onAdd={props.onCompareAdd}
                onRemove={props.onCompareRemove}
              />
            )}
            <Button
              variant={ButtonVariant.Secondary}
              size={ButtonSize.Small}
              data-testid={`AiForMeItemLearnMore-${displayName}`}
              touchpoint="learn-more"
              customData={getLearnMoreCustomData()}
              onClick={handleLearnMoreClick}
              aria-label={`learn more about ${displayName}`}
              icon={<IconExternalLink title={linkTitle} />}
              iconPosition={ButtonIconPosition.Trailing}
            >
              {getString("aiForMe.learnMore")}
            </Button>
          </Row>
        </Col>
      );
    }

    // old implementation
    return (
      <Row alignItems="center" justifyContent="space-between" gridGap={32} margin={"16px"}>
        <Button
          variant={ButtonVariant.Secondary}
          size={ButtonSize.Small}
          data-testid={`AiForMeItemLearnMore-${displayName}`}
          onClick={() => {
            props.onTrackClick(taxId);
            window.open(props.item.learnMoreLink, "_blank", "noopener,noreferrer");
          }}
          touchpoint="learn-more"
          customData={getLearnMoreCustomData()}
          aria-label={`learn more about ${displayName}`}
          icon={<IconExternalLink title={linkTitle} />}
          iconPosition={ButtonIconPosition.Trailing}
        >
          {getString("aiForMe.learnMore")}
        </Button>
        {isSubmittingVote && <Text fontSize="smaller">{getString("aiForMe.submitting")}</Text>}
        {errorMessage && <Text fontSize="smaller">{errorMessage}</Text>}
        {!isSubmittingVote && !errorMessage && (
          <Row alignItems="center" gridGap={5}>
            <Text fontSize="smaller">{getString("aiForMe.rateTool")}</Text>
            <StarRating name="star-rating" data-testid="star-rating" value={rating} onChange={handleVote} />
          </Row>
        )}
      </Row>
    );
  };

  return (
    <GridItem
      key={taxId.toString()}
      lg={props.gridSpan?.lg ?? 6}
      md={props.gridSpan?.md ?? 4}
      data-testid="AiForMeItem"
      data-index={props.index}
    >
      <Card style={{ minWidth: "100%" }}>
        <Col gridGap={20} style={{ minWidth: "100%", justifyContent: "space-between" }}>
          <>
            <Row alignItems="flex-start" justifyContent="space-between" gridGap={16}>
              <Col style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                <Row alignItems="flex-start" gridGap={5} style={{ flex: 1, overflow: "hidden" }}>
                  <Text
                    data-testid={`AiForMeItemTitle-${displayName}`}
                    fontSize="T300"
                    fontWeight={"bold"}
                    style={{ wordBreak: "break-word" }}
                  >
                    {displayName}
                  </Text>
                  {props.item.recommended && (
                    <IconCheckCircleFillWithTooltip
                      title="Show tooltip"
                      tooltipText="Recommended"
                      tooltipPosition={TOOLTIP_POSITION.BOTTOM}
                    />
                  )}
                </Row>
                <Row gridGap={8} style={{ flexShrink: 0 }}>
                  {renderRatings()}
                </Row>
                {props.lastUsedDate && (
                  <Text fontSize="T100" color="neutral70" data-testid={`last-used-date-${props.item.title}`}>
                    {getString("aiForMe.landingPage.recentlyUsedToolsSection.lastUsed", {
                      lastUsedPeriod: formatLastUsedDate(props.lastUsedDate),
                    })}
                  </Text>
                )}
              </Col>
              {toggleLists && (
                <ListActions
                  taxonomyId={props.item.taxonomyId}
                  lists={lists}
                  allowedDomain="aiforme"
                  toolName={displayName}
                />
              )}
            </Row>
            <Text
              data-testid={`AiForMeItemDescription-${displayName}`}
              color="var(--st-dt-color-text-secondary)"
              style={{ overflow: "auto" }}
            >
              {renderDescription()}
            </Text>
            {!props.hideTags && (
              <Row gridGap={8} flexWrap="wrap">
                {getTags()}
              </Row>
            )}
          </>
          <View style={{ marginTop: "auto" }}>
            <Hr />
            {renderFooter()}
          </View>
        </Col>
      </Card>
      {renderReviewSubmissionFlyout()}
      {openCheckReviewsFlyout && renderViewReviewsFlyout()}
    </GridItem>
  );
});
